/**
 * Chat for the call window that hosts a Fabric-embedded call.
 *
 * That window must exist (it owns the microphone, see `@/lib/meetingCallWindow`),
 * and the board panel already shows the avatar and captions, so this window is the
 * call's text side: the conversation so far, and a box for messages too long to say.
 * The agent answers aloud as usual; it never replies in this chat.
 *
 * Everything goes through Pipecat's RTVI client rather than our own wiring:
 * `usePipecatConversation` is the conversation store `PipecatClientProvider`
 * already keeps (user transcripts merged with `bot-output`), and a typed message is
 * `sendText`, which the server's `RTVIProcessor` appends to the LLM context and
 * answers aloud. `sendText` does not echo the message back, so it is injected into
 * the conversation here.
 */
import React, { useEffect, useRef, useState } from 'react'
import { Mic, MicOff, Phone, SendHorizontal } from 'lucide-react'
import {
  usePipecatClient,
  usePipecatClientMicControl,
  usePipecatConversation,
} from '@pipecat-ai/client-react'
import { useTranslation } from '@/i18n'
import type { CallConnectionStatus, CallPersona } from '@/lib/callChannel'
import { messageText } from '@/lib/callChat'

/** How close to the bottom still counts as "at the bottom" for following new messages. */
const FOLLOW_SLACK_PX = 48

const STATUS_DOT: Record<CallConnectionStatus, string> = {
  connected: 'bg-green-500',
  connecting: 'bg-amber-400',
  disconnected: 'bg-gray-500',
  error: 'bg-red-500',
}

interface CallChatProps {
  status: CallConnectionStatus
  persona: CallPersona | null
  onEnd: () => void
}

export const CallChat: React.FC<CallChatProps> = ({ status, persona, onEnd }) => {
  const { t } = useTranslation()
  const client = usePipecatClient()
  const { messages, injectMessage } = usePipecatConversation()
  // This window owns the microphone, so muting is pipecat's own mic control here.
  const { enableMic, isMicEnabled } = usePipecatClientMicControl()
  const micLabel = isMicEnabled
    ? t('digital_coworker:meetingBot.muteMic', { defaultValue: 'Mute' })
    : t('digital_coworker:meetingBot.unmuteMic', { defaultValue: 'Unmute' })
  const [draft, setDraft] = useState('')
  const listRef = useRef<HTMLDivElement>(null)

  const name = persona?.name ?? t('digital_coworker:meetingBot.agentAlt', { defaultValue: 'AI Agent' })
  // `system` lines come only from this component's `injectMessage` (a failed send):
  // the conversation store builds everything else from transcripts and bot output.
  const visible = messages
    .filter((m) => m.role === 'user' || m.role === 'assistant' || m.role === 'system')
    .map((m) => ({ role: m.role, createdAt: m.createdAt, text: messageText(m) }))
    .filter((m) => m.text)

  // Follow the conversation, including a bot reply that grows while it is spoken, but
  // only while the reader is at the bottom: someone scrolled up to an earlier answer
  // keeps their place.
  const followRef = useRef(true)
  const onScroll = () => {
    const list = listRef.current
    if (list) followRef.current = list.scrollHeight - list.scrollTop - list.clientHeight < FOLLOW_SLACK_PX
  }
  const lastText = visible[visible.length - 1]?.text
  useEffect(() => {
    const list = listRef.current
    if (list && followRef.current) list.scrollTop = list.scrollHeight
  }, [visible.length, lastText])

  // Typing always reaches the bot, listening off or not: `send-text` interrupts it like speech.
  const canSend = status === 'connected'

  const send = () => {
    const text = draft.trim()
    if (!text || !canSend) return
    setDraft('')
    // Your own message always brings you back to the latest.
    followRef.current = true
    injectMessage({ role: 'user', parts: [{ text, final: true, createdAt: new Date().toISOString() }] })
    client?.sendText(text).catch((err: unknown) => {
      console.error('[CallChat] sendText failed:', err)
      // The message already shows as sent and cannot be withdrawn, so give it back to resend.
      setDraft((current) => current || text)
      injectMessage({
        role: 'system',
        parts: [
          {
            text: t('digital_coworker:meetingBot.chatSendFailed', { defaultValue: "Couldn't send that message." }),
            final: true,
            createdAt: new Date().toISOString(),
          },
        ],
      })
    })
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault()
      send()
    }
  }

  return (
    <div className="h-screen w-full bg-gray-900 text-white flex flex-col">
      <div className="flex items-center gap-2 px-3 py-2 bg-gray-800 border-b border-gray-700">
        {persona?.image ? (
          <img src={persona.image} alt="" className="w-8 h-8 rounded-full object-cover" />
        ) : (
          <div className="w-8 h-8 rounded-full bg-gray-700" />
        )}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">{name}</p>
          <p className="flex items-center gap-1.5 text-xs text-gray-400">
            <span aria-hidden className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT[status]}`} />
            {t(`digital_coworker:meetingBot.${status}`)}
          </p>
        </div>
        <button
          onClick={() => enableMic(!isMicEnabled)}
          disabled={status !== 'connected'}
          className={`p-1.5 rounded disabled:opacity-50 ${
            isMicEnabled ? 'text-gray-300 hover:bg-gray-700 hover:text-white' : 'bg-gray-700 text-red-400'
          }`}
          aria-label={micLabel}
          title={micLabel}
        >
          {isMicEnabled ? <Mic className="w-4 h-4" /> : <MicOff className="w-4 h-4" />}
        </button>
        <button
          onClick={onEnd}
          className="p-1.5 rounded bg-red-600 hover:bg-red-700 text-white"
          aria-label={t('digital_coworker:meetingBot.endCall', { defaultValue: 'End call' })}
          title={t('digital_coworker:meetingBot.endCall', { defaultValue: 'End call' })}
        >
          <Phone className="w-4 h-4 rotate-[135deg]" />
        </button>
      </div>

      <div ref={listRef} onScroll={onScroll} className="flex-1 min-h-0 overflow-y-auto px-3 py-3 flex flex-col gap-2" aria-live="polite">
        {visible.length === 0 ? (
          <p className="m-auto max-w-[240px] text-center text-xs text-gray-400">
            {t('digital_coworker:meetingBot.chatEmpty', {
              name,
              defaultValue: 'Type here for anything too long to say. {{name}} answers out loud.',
            })}
          </p>
        ) : (
          visible.map((m, i) =>
            m.role === 'system' ? (
              <p key={`${m.createdAt}-${i}`} className="self-center text-xs text-gray-400">
                {m.text}
              </p>
            ) : (
              <p
                key={`${m.createdAt}-${i}`}
                className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap break-words ${
                  m.role === 'user'
                    ? 'self-end bg-blue-600 rounded-br-sm'
                    : 'self-start bg-gray-800 text-gray-100 rounded-bl-sm'
                }`}
              >
                {m.text}
              </p>
            )
          )
        )}
      </div>

      <div className="px-3 pt-2 pb-3 border-t border-gray-700">
        <div className="flex items-end gap-2 rounded-xl bg-gray-800 border border-gray-700 focus-within:border-blue-500 px-3 py-2">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKeyDown}
            rows={1}
            disabled={status !== 'connected'}
            placeholder={t('digital_coworker:meetingBot.chatPlaceholder', {
              name,
              defaultValue: 'Type a message to {{name}}',
            })}
            aria-label={t('digital_coworker:meetingBot.chatPlaceholder', {
              name,
              defaultValue: 'Type a message to {{name}}',
            })}
            className="flex-1 max-h-28 resize-none bg-transparent text-sm text-white placeholder:text-gray-500 focus:outline-none disabled:opacity-50"
          />
          <button
            onClick={send}
            disabled={!canSend || !draft.trim()}
            className="p-1 rounded text-blue-400 hover:text-blue-300 disabled:text-gray-600"
            aria-label={t('digital_coworker:meetingBot.send', { defaultValue: 'Send' })}
          >
            <SendHorizontal className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  )
}
